import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { TransportExtension, UsageProfile } from '@thive/fddm-types';

import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { assertNever } from '@/helpers/utils';

import { UsageProfileOptions } from '../types';

import {
  setConsolidationAmountPreference,
  setFlowLimitPreference,
  setFlowLoadsPreference,
} from '../preferences';
import { isConsolidationFlowType } from '../guards';

export const useFlowUpdaters = (id: string) => {
  const { t } = useTranslation();
  const { getFlowById, getPreference } = useFloorplanDesignStore.getState();

  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const onNameChange = useCallback(
    (value: string) => {
      if (!value) {
        return { error: t('errors:flow.name_required') };
      }

      const { flows } = useFloorplanDesignStore.getState().design.flows;
      const unique = flows.filter((item) => item.id !== id).every((item) => item.name !== value);
      if (!unique) {
        return { error: t('errors:flow.name_duplicated') };
      }

      const flow = getFlowById(id);
      if (!flow) {
        return { error: t('errors:flow.not_found') };
      }

      fddmSvc.updateFlow({
        ...flow,
        name: value,
      });
    },
    [id, t, getFlowById, fddmSvc],
  );

  const onFlowVehicleLimitChange = useCallback(
    (value: number) => {
      const flow = getFlowById(id);
      if (!flow) {
        return { error: t('errors:flow.not_found') };
      }

      setFlowLimitPreference(flow.type, value);
      fddmSvc.updateFlow({
        ...flow,
        vehicleLimit: value,
      });
    },
    [fddmSvc, getFlowById, id, t],
  );

  const onFlowLoadsChange = useCallback(
    (value: number) => {
      const flow = getFlowById(id);
      if (!flow) {
        return { error: t('errors:flow.not_found') };
      }
      const hourlyOrderCounts: [number, ...number[]] = [value, ...new Array(24).fill(value)];

      setFlowLoadsPreference(flow.type, hourlyOrderCounts);
      fddmSvc.updateFlow({
        ...flow,
        hourlyOrderCounts,
      });
    },
    [fddmSvc, getFlowById, id, t],
  );

  const onPreAnnounceDurationChange = useCallback(
    (value: number) => {
      const flow = getFlowById(id);
      if (!flow) {
        return { error: t('errors:flow.not_found') };
      }

      switch (flow.type) {
        case 'TRANSPORT': {
          fddmSvc.setPreference('flow.transportExtension.preAnnounceDuration', value);

          // if value is 0, we want to set transportExtension to undefined
          let transportExtension: TransportExtension | undefined;
          if (value <= 0) {
            transportExtension = undefined;
          } else if (flow.transportExtension) {
            transportExtension = {
              ...flow.transportExtension,
              preAnnounceDuration: value,
            };
          } else {
            transportExtension = {
              preAnnounceDuration: value,
              maxActivePreAnnouncedAvOrders: getPreference(
                'flow.transportExtension.maxActivePreAnnouncedAvOrders',
              ),
            };
          }

          fddmSvc.updateFlow({
            ...flow,
            transportExtension,
          });
          break;
        }
        case 'CONSOLIDATION_STORAGE':
          if (!flow.consolidationStorageExtension) {
            console.error(
              `Consolidation storage flow with id ${id} does not have a consolidationStorageExtension`,
            );
            return;
          }

          fddmSvc.setPreference('flow.consolidationStorageExtension.preAnnounceDuration', value);
          fddmSvc.updateFlow({
            ...flow,
            consolidationStorageExtension: {
              ...flow.consolidationStorageExtension,
              preAnnounceDuration: value,
            },
          });
          break;
        default:
          return { error: t('errors:flow.invalid_type') };
      }
    },
    [fddmSvc, getFlowById, getPreference, id, t],
  );

  const onMaxActivePreAnnouncedAvOrdersChange = useCallback(
    (value: number) => {
      const flow = getFlowById(id);
      if (!flow) {
        return { error: t('errors:flow.not_found') };
      }

      switch (flow.type) {
        case 'TRANSPORT':
          if (!flow.transportExtension) {
            console.error(`Transport flow with id ${id} does not have a transportExtension`);
            return;
          }

          fddmSvc.setPreference('flow.transportExtension.maxActivePreAnnouncedAvOrders', value);
          fddmSvc.updateFlow({
            ...flow,
            transportExtension: {
              ...flow.transportExtension,
              maxActivePreAnnouncedAvOrders: value,
            },
          });
          break;
        case 'CONSOLIDATION_STORAGE':
          if (!flow.consolidationStorageExtension) {
            console.error(
              `Consolidation storage flow with id ${id} does not have a consolidationStorageExtension`,
            );
            return;
          }

          fddmSvc.setPreference(
            'flow.consolidationStorageExtension.maxActivePreAnnouncedAvOrders',
            value,
          );
          fddmSvc.updateFlow({
            ...flow,
            consolidationStorageExtension: {
              ...flow.consolidationStorageExtension,
              maxActivePreAnnouncedAvOrders: value,
            },
          });
          break;
        default:
          return { error: t('errors:flow.invalid_type') };
      }
    },
    [fddmSvc, getFlowById, id, t],
  );

  const onConsolidationGroupsChange = useCallback(
    (newConsolidationGroupAmount: number) => {
      const flow = getFlowById(id);
      if (!flow) return;

      if (!isConsolidationFlowType(flow.type)) return;
      setConsolidationAmountPreference(flow.type, newConsolidationGroupAmount);

      if (flow.consolidationStorageExtension) {
        fddmSvc.updateFlow({
          ...flow,
          consolidationStorageExtension: {
            ...flow.consolidationStorageExtension,
            numConsolidations: newConsolidationGroupAmount,
          },
        });

        return;
      }

      if (!flow.consolidationRetrievalExtension) return;

      fddmSvc.updateFlow({
        ...flow,
        consolidationRetrievalExtension: {
          ...flow.consolidationRetrievalExtension,
          numConsolidations: newConsolidationGroupAmount,
        },
      });
    },
    [fddmSvc, getFlowById, id],
  );

  const onBatchSizeChange = useCallback(
    (newTotalBatchSize: number) => {
      const flow = getFlowById(id);
      if (!flow) return;

      if (!flow.consolidationStorageExtension) return;
      fddmSvc.setPreference('flow.consolidationStorageExtension.batchSize', newTotalBatchSize);
      fddmSvc.updateFlow({
        ...flow,
        consolidationStorageExtension: {
          ...flow.consolidationStorageExtension,
          batchSize: newTotalBatchSize,
        },
      });
    },
    [fddmSvc, getFlowById, id],
  );

  const onShipmentSizeChange = useCallback(
    (newShipmentSize: number) => {
      const flow = getFlowById(id);
      if (!flow) return;

      if (!flow.consolidationRetrievalExtension) return;
      fddmSvc.setPreference('flow.consolidationRetrievalExtension.shipmentSize', newShipmentSize);
      fddmSvc.updateFlow({
        ...flow,
        consolidationRetrievalExtension: {
          ...flow.consolidationRetrievalExtension,
          shipmentSize: newShipmentSize,
        },
      });
    },
    [fddmSvc, getFlowById, id],
  );

  const onRetrievalStartDelayChange = useCallback(
    (newRetrievalStartDelay: number) => {
      const flow = getFlowById(id);
      if (!flow) return;

      if (!flow.consolidationRetrievalExtension) return;
      fddmSvc.setPreference(
        'flow.consolidationRetrievalExtension.retrievalStartDelayInHours',
        newRetrievalStartDelay,
      );
      fddmSvc.updateFlow({
        ...flow,
        consolidationRetrievalExtension: {
          ...flow.consolidationRetrievalExtension,
          retrievalStartDelayInHours: newRetrievalStartDelay,
        },
      });
    },
    [getFlowById, id, fddmSvc],
  );

  const onGoodsToPersonsProfileValueChange = useCallback(
    (option: UsageProfileOptions, key: 'startWeight' | 'endWeight' | 'rate', value: number) => {
      const flow = getFlowById(id);
      if (!flow) return;

      const updateUsageProfile = (current: UsageProfile): UsageProfile => {
        const { type, usageProfileExponential, usageProfileLinear } = current;

        if (type === 'LINEAR') {
          if (key === 'rate') {
            return current;
          }

          if (!usageProfileLinear) return current;
          fddmSvc.setPreference(
            `flow.goodsToPersonsExtension.${option}.usageProfileLinear.${key}`,
            value,
          );
          return {
            ...current,
            usageProfileLinear: {
              ...usageProfileLinear,
              [key]: value,
            },
          };
        }

        if (type === 'EXPONENTIAL') {
          if (key !== 'rate') {
            return current;
          }

          fddmSvc.setPreference(
            `flow.goodsToPersonsExtension.${option}.usageProfileExponential.${key}`,
            value,
          );
          return {
            ...current,
            usageProfileExponential: {
              ...usageProfileExponential,
              rate: value,
            },
          };
        }

        return current;
      };

      if (!flow.goodsToPersonsExtension || !flow.goodsToPersonsExtension[option]) return;
      const currentProfile = flow.goodsToPersonsExtension[option];
      if (!currentProfile) return;
      fddmSvc.updateFlow({
        ...flow,
        goodsToPersonsExtension: {
          ...flow.goodsToPersonsExtension,
          [option]: updateUsageProfile(currentProfile),
        },
      });
    },
    [fddmSvc, getFlowById, id],
  );

  /** @description updates usage profile through the dropdown...  */
  const updateUsageProfile = useCallback(
    (
      current: UsageProfile,
      newType: UsageProfile['type'],
      option: UsageProfileOptions,
    ): UsageProfile => {
      switch (newType) {
        case 'LINEAR':
          return current.type === 'LINEAR'
            ? current
            : {
                type: 'LINEAR',
                usageProfileLinear: {
                  startWeight: getPreference(
                    `flow.goodsToPersonsExtension.${option}.usageProfileLinear.startWeight`,
                  ),
                  endWeight: getPreference(
                    `flow.goodsToPersonsExtension.${option}.usageProfileLinear.endWeight`,
                  ),
                },
              };

        case 'EXPONENTIAL':
          return current.type === 'EXPONENTIAL'
            ? current
            : {
                type: 'EXPONENTIAL',
                usageProfileExponential: {
                  rate: getPreference(
                    `flow.goodsToPersonsExtension.${option}.usageProfileExponential.rate`,
                  ),
                },
              };

        default:
          assertNever(newType);
      }
    },
    [getPreference],
  );

  const onGoodsToPersonsProfileTypeChange = useCallback(
    (option: UsageProfileOptions, value: UsageProfile['type']) => {
      const flow = getFlowById(id);
      if (!flow) return;

      if (!flow.goodsToPersonsExtension || !flow.goodsToPersonsExtension[option]) return;

      const currentProfile = flow.goodsToPersonsExtension[option];
      if (!currentProfile) return;

      fddmSvc.updateFlow({
        ...flow,
        goodsToPersonsExtension: {
          ...flow.goodsToPersonsExtension,
          [option]: updateUsageProfile(currentProfile, value, option),
        },
      });
    },
    [fddmSvc, getFlowById, id, updateUsageProfile],
  );

  const onHandoverCooldownChange = useCallback(
    (newHandoverCooldown: number) => {
      const flow = getFlowById(id);
      if (!flow) return;
      if (!flow.goodsToPersonsExtension) return;

      fddmSvc.updateFlow({
        ...flow,
        goodsToPersonsExtension: {
          ...flow.goodsToPersonsExtension,
          handoverCooldown: newHandoverCooldown,
        },
      });
    },
    [getFlowById, id, fddmSvc],
  );

  const onConcurrentAisleCountChange = useCallback(
    (newConcurrentAisleCount: number) => {
      const flow = getFlowById(id);
      if (!flow) return;
      if (!flow.goodsToPersonsExtension) return;

      fddmSvc.updateFlow({
        ...flow,
        goodsToPersonsExtension: {
          ...flow.goodsToPersonsExtension,
          concurrentAisleCount: newConcurrentAisleCount,
        },
      });
    },
    [fddmSvc, getFlowById, id],
  );

  const onProcessDurationChange = useCallback(
    (newProcessDuration: number) => {
      const flow = getFlowById(id);
      if (!flow) return;

      if (flow.goodsToPersonsExtension) {
        fddmSvc.updateFlow({
          ...flow,
          goodsToPersonsExtension: {
            ...flow.goodsToPersonsExtension,
            processDuration: newProcessDuration,
          },
        });

        return;
      }

      if (!flow.externalFlowExtension) return;

      fddmSvc.updateFlow({
        ...flow,
        externalFlowExtension: {
          ...flow.externalFlowExtension,
          processDuration: newProcessDuration,
        },
      });
    },
    [getFlowById, id, fddmSvc],
  );

  const onSequencingHorizonWindowChange = useCallback(
    (newSequencingHorizonWindow: number) => {
      const flow = getFlowById(id);
      if (!flow) return;
      if (!flow.goodsToPersonsExtension) return;

      fddmSvc.updateFlow({
        ...flow,
        goodsToPersonsExtension: {
          ...flow.goodsToPersonsExtension,
          sequencingHorizonWindow: newSequencingHorizonWindow,
        },
      });
    },
    [getFlowById, id, fddmSvc],
  );

  return {
    onNameChange,
    onFlowVehicleLimitChange,
    onFlowLoadsChange,
    onPreAnnounceDurationChange,
    onMaxActivePreAnnouncedAvOrdersChange,
    onConsolidationGroupsChange,
    onBatchSizeChange,
    onShipmentSizeChange,
    onRetrievalStartDelayChange,
    onGoodsToPersonsProfileValueChange,
    updateUsageProfile,
    onGoodsToPersonsProfileTypeChange,
    onConcurrentAisleCountChange,
    onHandoverCooldownChange,
    onProcessDurationChange,
    onSequencingHorizonWindowChange,
  };
};
